import { IAd, IServices, ISlot } from '../../uanc.types';
import { filterBy, isArrayType, isFunctionType, isStringType } from '../../helpers/js-utils';
import {
    AD_SERVERS,
    AD_STATUS,
    ADS_LOADED_EVENT,
    MULTI_PLACE_SLOTS,
    SERVICES_GLOBAL_ENABLING,
    STATE_SERVICE,
} from '../../configs/constants';
import { prepareSlotDfp } from './DFP/model-slot-dfp';
import UancEvent from '../UancEvent';
import DebugUANC from '../../core/version/debug';
import { serviceDFPUtils } from './DFP/service-dfp';
import { serviceADOCEANUtils } from './ADOCEAN/service-adocean';
import {
    IAdsToEmbed,
    IArtSponsParams,
    ITimers,
    ServiceSlotsType,
    ServiceSlotType,
    ServiceType,
    ServiceUtilsType,
    TargetingType,
} from './ad-services.types';
import { IDFPService, IDFPServiceSlot } from './DFP/service-dfp.types';
import { IADOCEANService, IAOServiceSlot } from './ADOCEAN/service-adocean.types';
import { prepareSlotAdOcean } from './ADOCEAN/model-slot-adocean';
import { iwaAddEvent } from '../iwa-add-event';
import { isElemInView } from '../../helpers/elements-visibility';
import { getWindowContext } from '../../core/pre-config/pre-config.utils';

/**
 * Na podstawie konfiguracji slotów, weryfikuje ad serwery biorące udział w osadzaniu reklam.
 * W przypadku gdy dany ad serwer nie jest wykorzystywany, ustawia state serwisu - REJECTED.
 *
 * @returns {AD_SERVERS[]} nazwy adserverów
 */
export const getAdServersToProcess = (): AD_SERVERS[] => {
    const { config, UANC } = window.Inpl.Ad;
    const adServers = config?.adServers as AD_SERVERS[];
    if (!adServers.length) {
        Object.keys(AD_SERVERS).forEach((adServer) => setAdServicesState(AD_SERVERS[adServer as AD_SERVERS], STATE_SERVICE.REJECTED));
        UANC instanceof DebugUANC && UANC.log('No ad servers to use');
        return [];
    }

    const adServersToEmbed = adServers.filter((adServerName) => adServerName in SERVICES_GLOBAL_ENABLING);
    if (adServersToEmbed.length !== Object.keys(AD_SERVERS).length) {
        const notUsedAdServers: AD_SERVERS[] = (Object.keys(AD_SERVERS) as AD_SERVERS[]).filter(
            (adServer) => !adServersToEmbed.includes(adServer)
        );
        notUsedAdServers.forEach((notUsedAdServer) => setAdServicesState(AD_SERVERS[notUsedAdServer], STATE_SERVICE.REJECTED));
    }
    return adServersToEmbed;
};

/**
 * Ustawia informację o stanie poszczególnych serwisów. Wywołuje globalny event 'Inpl.adsLoaded'.
 *
 * @param {AD_SERVERS} adServer - jakiego adserwera event dotyczy
 * @param {STATE_SERVICE} serviceState - stan serwisu
 * @returns {void}
 */
export const setAdServicesState = (adServer: AD_SERVERS, serviceState: STATE_SERVICE) => {
    const { event, state, UANC } = window.Inpl.Ad;

    const serviceStateName = adServer === AD_SERVERS.DFP ? 'DFPServiceState' : 'ADOCEANServiceState';
    UANC?.setState({ [serviceStateName]: serviceState });
    if (serviceState !== STATE_SERVICE.PENDING)
        UANC!.unresolvedServices = UANC!.unresolvedServices.filter((service) => service !== adServer);

    // sprawdzenie czy oba serwisy uaktualniły status
    if (state!.ADOCEANServiceState !== STATE_SERVICE.PENDING && state!.DFPServiceState !== STATE_SERVICE.PENDING) {
        UANC!.unresolvedServices = UANC!.unresolvedServices.filter((service) => !Object.keys(SERVICES_GLOBAL_ENABLING).includes(service));
        window.Inpl.Ad.services = {
            ...window.Inpl.Ad.services,
            resolved: true,
        };

        // ustawienie statusu o zakończeniu odświeżania
        if (state?.isReloadUanc) UANC?.setState({ isReloadUanc: false });

        // globalny event o stanie gotowości serwisów - 'Inpl.adsLoaded'
        event?.custom(ADS_LOADED_EVENT, { services: window.Inpl.Ad.services });
    }
};

/**
 * Zwraca funkcje serwisowe / dostępne globalnie dla konkretnego serwera.
 *
 * @param {AD_SERVERS} adServer - nazwa adservera
 * @returns {ServiceUtilsType} funkcje per serwis
 */
export const getServiceUtils = (adServer: AD_SERVERS): ServiceUtilsType => {
    if (adServer === AD_SERVERS.DFP) return serviceDFPUtils;
    else return serviceADOCEANUtils;
};

/**
 * Waliduje ustawienia targetingu.
 *
 * @param {TargetingType} targeting - ustawienia targetowania
 * @returns {void}
 */
export const checkTargetingSetting = (targeting: TargetingType): void => {
    if (!isArrayType(targeting)) throw new Error(`Targeting param must be an Array instead of ${typeof targeting}`);
    if (!targeting[0] || !isStringType(targeting[0])) throw new Error('Targeting key must be a string type.');
    if (!targeting[1] || (!isStringType(targeting[1]) && !isArrayType(targeting[1])))
        throw new Error('Targeting values must be a string or an array.');
};

/**
 * Walidacja wykorzystywana podczas użycia gobalnych metod slotów reklamowych. Sprawdza podstawowe pola - nazwę oraz targeting.
 *
 * @param {string} slotName - nazwa slotu
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienia targetowania
 * @returns {void}
 */
export const verifyAdParams = (slotName: string, targeting: TargetingType | null = null): void => {
    if (!slotName || !isStringType(slotName)) {
        throw new Error(`Slot name must be a string instead of "${slotName}"`);
    }

    if (targeting) checkTargetingSetting(targeting);
};

/**
 * Aktualizacja miejsca osadzenia slotu w przypadku przekazania innego div id niż aktualnie w konfiguracji.
 *
 * @param {string} slotName - nazwa slotu
 * @param {string[]} targetDivsIds - nazwa id kontenera gdzie ma być osadzony slot
 * @returns {void}
 */
export const updateSlotPlacement = (slotName: string, targetDivsIds: string[]): void => {
    const existedSlot = getAvailableSlot(slotName);
    if (!existedSlot) return;

    existedSlot.placementIds = targetDivsIds;
};

/**
 * Przekazuje sloty odpowiednie dla wskazanego adServera.
 *
 * @param {AD_SERVERS} adServerType - typ adservera (DFP | AO)
 * @param {ISlot[]} slots - sloty
 * @returns {ISlot[]} sloty
 */
export const getSlotsByAdServer = (adServerType: AD_SERVERS, slots: ISlot[]): ISlot[] => {
    return filterBy(slots, 'adServer', adServerType);
};

/**
 * Przygotowuje wszystkie sloty dla danego adServera.
 *
 * @param {AD_SERVERS} adServerType - typ adservera (DFP | AO)
 * @param {ISlot[]} slots - sloty
 * @returns {ISlot[]} sloty
 */
export const buildServiceSlotsConfig = (adServerType: AD_SERVERS, slots: ISlot[]): ServiceSlotsType => {
    return slots.map((slot) => (adServerType === AD_SERVERS.DFP ? prepareSlotDfp(slot) : prepareSlotAdOcean(slot))) as ServiceSlotsType;
};

/**
 * Dodanie eventów w zależności od stanu slotu.
 *
 * @param {Partial<IDFPServiceSlot> | Partial<IAOServiceSlot>} slot - obiekt slotu lub fragment identyfikujący slot - minimum pole name i placementIds
 * @param {string | null} placementName - nazwa placementu, którego zdarzenie dotyczy. ArtSpons nie określają placement - null
 * @param {AD_STATUS} state - stan reklamy
 *
 * @returns {void}
 */
export const addSlotEvent = (
    slot: Partial<IDFPServiceSlot> | Partial<IAOServiceSlot>,
    placementName: string | null,
    state: AD_STATUS
): void => {
    const { event, config, testAB, state: UancState } = window.Inpl.Ad;
    const uancEvent = event as UancEvent;

    const slotInfo = {
        name: slot.name,
        placementId: placementName,
    };
    const isSlotMultiPlaces = MULTI_PLACE_SLOTS.includes(<string>slotInfo.name);

    if (state === AD_STATUS.LOADED) {
        uancEvent.custom(`Inpl.${slotInfo.name}.loaded`, {
            // adServer, currentSize - informacje potrzebna dla inpl.tiled-mixer-injection
            slot: {
                ...slotInfo,
                adServer: slot.adServer,
                currentSize: (placementName && slot.renderedPlacements?.[placementName]?.currentSize) || [0, 0],
            },
        });
        uancEvent.custom('Inpl.slotLoaded', slotInfo); // event wykorzystywany m.in w inpl.ads-article / inpl.ads-sidebar

        iwaAddEvent(
            'trackEvent',
            'uanc',
            'slots',
            'loaded',
            slotInfo.name!,
            isSlotMultiPlaces ? placementName! : '',
            testAB?.currentTest ?? '',
            testAB?.currentTest ? JSON.stringify(config!.keywords) : ''
        );
    } else if (state === AD_STATUS.ART_SPONS) {
        uancEvent.custom(`Inpl.${AD_STATUS.ART_SPONS}`, {
            slot,
        });
    } else if (state === AD_STATUS.EMPTY) {
        uancEvent.custom(`Inpl.${AD_STATUS.EMPTY}`, slotInfo);

        UancState?.isAdBlockDetect &&
            iwaAddEvent(
                'trackEvent',
                'uanc',
                'slots',
                'adblock',
                slotInfo.name!,
                isSlotMultiPlaces ? placementName! : '',
                testAB?.currentTest ?? '',
                testAB?.currentTest ? JSON.stringify(config!.keywords) : ''
            );
    }
};

/**
 * Zwraca slot o podanej nazwie o ile istnieje w przekazanej tablicy slotów.
 *
 * @param {string} slotName - nazwa slotu
 * @param {ISlot[] | ServiceSlotsType} slots - sloty
 * @returns {ISlot | IDFPServiceSlot | IAOServiceSlot | null } slot
 */
export const findSlot = <T extends { name: string }>(slotName: string, slots: T[]): T | null => {
    return slots.find((slot) => slot.name === slotName) ?? null;
};

/**
 * Zwraca konfigurację slotu zawierającego wskazaną jednostkę reklamową.
 *
 * @param {string} adUnitName - nazwa jednostki reklamowej
 * @param {ServiceSlotsType} slots - sloty
 * @returns {ServiceSlotType | null } slot
 */
export const findSlotByAdUnitName = (adUnitName: string, slots: ServiceSlotsType): ServiceSlotType | null => {
    return slots.find((slot: ServiceSlotType) => slot.placementIds?.includes(adUnitName)) ?? null;
};

/**
 * Zwraca włączone serwisy - ADOCEANService / DFPService.
 *
 * @returns {string[]} nazwy serwisów
 */
export const getEnabledAdServices = (): string[] => {
    const { services } = window.Inpl.Ad;
    if (services) {
        return Object.keys(services).filter((serviceKey) => {
            if (serviceKey === 'ADOCEANService' || serviceKey === 'DFPService') {
                const service = services[serviceKey as keyof IServices] as IADOCEANService | IDFPService;
                return service.isEnabled;
            }
            return false;
        });
    }
    return [];
};

/**
 * Zwraca nazwy wszystkich slotów dla aktywnych serwisów - pomija sitebtanding.
 *
 * @returns {string[]} nazwy slotów
 */
export const getAllSlotNames = (): string[] => {
    const { config } = window.Inpl.Ad;
    const slots = config?.slots || [];
    const enabledAdServices = getEnabledAdServices();
    return slots
        .filter((slot: ISlot) => {
            const serviceName = `${slot.adServer}Service`;
            return enabledAdServices.includes(serviceName) && slot.name !== 'sitebranding';
        })
        .map((slot: ISlot) => slot.name);
};

/**
 * Zwraca nazwy jednostek reklamowych możliwych do odświeżenia. Pomija sloty testowe.
 * Weryfikuje czy slot istnieje i jest aktywny. Sprawdza poszczególne placementy czy znajdują się w viewport.
 *
 * @param {string[]} slotNames - nazwy slotów
 * @param {AD_SERVERS} adServer - nazwa adserwera
 * @param {boolean} isIndicatedSlots - czy sloty zostały wskazane do odświeżenia
 *
 * @returns {string[]} nazwy jednostek
 */
export const getAdUnitNamesToRefresh = (slotNames: string[], adServer: AD_SERVERS, isIndicatedSlots: boolean): string[] => {
    const { Ad } = window.Inpl;
    const { services } = window.Inpl.Ad;
    const service = services?.[`${adServer}Service`] as ServiceType;

    return slotNames.reduce((slots: string[], slotName: string) => {
        const serviceSlot = findSlot<IDFPServiceSlot | IAOServiceSlot>(slotName, service.slots);
        if (serviceSlot && !serviceSlot.isTest && serviceSlot.placementIds.length) {
            const placementNamesInView: string[] = serviceSlot.placementIds.filter((placementId: string) => {
                const placementDOM: HTMLElement | null = getWindowContext().document.getElementById(placementId);
                if (!placementDOM) {
                    // wskazany placement nie znajduje się w DOM, pomijany do refresha
                    isIndicatedSlots &&
                        Ad.UANC instanceof DebugUANC &&
                        Ad.UANC.warn(`[refreshAds] Failed to refresh a placement: ${placementId} - missing placement in DOM.`);
                    return false;
                }

                // sprawdzenie czy placement znajduje się w viewport
                return isElemInView(placementDOM);
            });
            return [...slots, ...placementNamesInView];
        }
        return slots;
    }, []);
};

/**
 * Sprawdza czy slot o podanej nazwie został skonfigurowany w adSet (pobrany z ADC) i może być osadzany w serwisie.
 * Zwraca "slot serwisowy" jeżeli istnieje i jest aktywny (włączony + ustawiony adserver).
 * Dodatkowo weryfikuje czy usługi dla adservera zostały zainicjowane oraz czy globalnie adserver nie został wyłączony.
 *
 * @param {string} slotName - nazwa slotu
 * @param {string[] | null} newPlacementIds - opcjonalnie, nazwa id nowego kontenera gdzie ma być osadzony slot
 *
 * @returns {IDFPServiceSlot | IAOServiceSlot | null } konfiguracja slotu
 */
export const getAvailableSlot = (slotName: string, newPlacementIds: string[] | null = null): ServiceSlotType | null => {
    const { config, UANC, services } = window.Inpl.Ad;
    let isServiceSlot: ServiceSlotType | null = null;
    try {
        const isSlotConfig = findSlot(slotName, config!.slots);
        if (!isSlotConfig?.adServer) {
            throw new Error(`"${slotName}" is not exist in config or has an invalid configuration (adServer).`);
        }

        const service = services?.[`${isSlotConfig.adServer}Service`] as ServiceType;
        const isGlobalEnabled = SERVICES_GLOBAL_ENABLING[isSlotConfig.adServer];
        if (!isGlobalEnabled.isEnabled || !service?.isEnabled) {
            throw new Error(`"${isSlotConfig.adServer}" ad service is not enabled`);
        }
        isServiceSlot = findSlot<IDFPServiceSlot | IAOServiceSlot>(slotName, service.slots);
        if (isServiceSlot?.disable) {
            throw new Error(`"${slotName}" is disabled.`);
        }

        return isServiceSlot as ServiceSlotType;
    } catch (error) {
        // Próba osadzenia slotu, który jest wyłączony / nieskonfigurowany. Informacja o empty slocie - Inpl.empty, aby ukryć placement po stronie serwisu.
        if (isServiceSlot) {
            const placementIds = newPlacementIds?.length
                ? mergePlacements(isServiceSlot.name, isServiceSlot.placementIds, newPlacementIds)
                : isServiceSlot.placementIds;
            sendEmptyEventDisableSlots({ name: slotName, placementIds });
        } else {
            newPlacementIds?.length && sendEmptyEventDisableSlots({ name: slotName, placementIds: newPlacementIds });
        }
        UANC instanceof DebugUANC &&
            UANC.error(
                `[getAvailableSlot] Problem with checking slot availability: ${slotName}. ${error instanceof Error ? error.message : error}`
            );
        return null;
    }
};

/**
 * Zwraca nazwy dostępnych slotów z podziałem na konkretne adserwery.
 *
 * @param {string[]} slotsNames - nazwy slotów
 * @returns {ISlot | undefined } slot
 */

export interface IGroupSlotsByAdServer {
    [AD_SERVERS.DFP]?: string[];
    [AD_SERVERS.ADOCEAN]?: string[];
}

export const getSlotNamesByAdServer = (slotsNames: string[]): IGroupSlotsByAdServer => {
    return slotsNames.reduce((group, slotName) => {
        const slot = getAvailableSlot(slotName);
        if (!slot) return group;

        if (slot.adServer in group) {
            group[slot.adServer]!.push(slotName);
            return group;
        } else {
            return {
                ...group,
                [slot.adServer]: [slotName],
            };
        }
    }, {} as IGroupSlotsByAdServer);
};

/**
 * Wywołanie funkcji cb przekazanej podczas osadzania reklam (insertAds)
 *
 * @param {string[]} slotsNames - nazwy slotów
 * @param {() => void} cb - callback do wywołania po osadzeniu reklam
 * @returns {void}
 */
export const executeCbFn = (slotsNames: string[], cb: () => void) => {
    const event = window.Inpl.Ad.event as UancEvent;
    const slotsEvents: Promise<any>[] = [];

    slotsNames.forEach((name, idx) => {
        slotsEvents.push(
            new Promise((resolve) => {
                event.add(`Inpl.${name}`, window, () => resolve(name));
            })
        );

        if (slotsNames.length - 1 === idx) {
            Promise.all(slotsEvents).then(() => {
                cb();
            });
        }
    });
};

/**
 *  Wywołanie przypisanego callbacku dla linku sponsorowanego.
 *
 * @param {string} name - nazwa - linkSpons
 * @param {boolean} isDFP - opcjonalnie, określenie adserwera
 * @returns {void}
 */
export const executeArtSponsCb = (name: string, isDFP: boolean = false): void => {
    const { UANC } = window.Inpl.Ad;
    const services = window.Inpl.Ad.services as IServices;
    const service = isDFP ? services.DFPService : services.ADOCEANService;

    if (service && name) {
        const { data, dataRequests } = service.linkSponsPendingItems;
        const callback = dataRequests[name];
        if (callback && isFunctionType(callback)) {
            try {
                callback(data[name]);
            } catch (e) {
                UANC instanceof DebugUANC && UANC.warn('Problem with callback execution for linkSpons', e);
            }
        }
    }
};

/**
 * Dodanie nowych placementów do listy jeżeli prędzej nie istniały.
 *
 * @param {string} slotName - nazwa slotu
 * @param {string[]} currentPlacementIds - aktualna lista placementów
 * @param {string[]} newPlacements - nazwy nowych miejsc
 *
 * @returns {string[]} zaktualizowana lista placementów
 */
export const mergePlacements = (slotName: string, currentPlacementIds: string[], newPlacements: string[]): string[] => {
    return newPlacements.reduce(
        (mergedPlacements: string[], newPlacement: string) =>
            mergedPlacements.includes(newPlacement)
                ? mergedPlacements
                : [...mergedPlacements, newPlacement].filter((placement) => placement !== `ad-${slotName}`),
        [...currentPlacementIds]
    );
};

/**
 * Wysyła event z informacją o wyłączonym slocie.
 *
 * @param {Partial<IDFPServiceSlot> | Partial<IAOServiceSlot>} slot - slot
 * @returns {void}
 */
export const sendEmptyEventDisableSlots = (slot: Partial<IDFPServiceSlot> | Partial<IAOServiceSlot>): void => {
    slot.placementIds?.forEach((id) => {
        addSlotEvent(slot, id, AD_STATUS.EMPTY);
    });
};

/**
 *  Zapisuje do serwisu ADOCEAN konfigurację reklamy dla artykułu sponsorowanego / linku sponsorowanego w zależności od servera.
 *
 * @param serviceKey - nazwa Adservera
 * @param {string} name - nazwa - linkSpons
 * @param {IArtSponsParams | null} data - konfiguracja
 * @param isDFP
 * @returns {void}
 */
export const setArtSponsParamsFromService = (
    serviceKey: keyof Pick<IServices, 'DFPService' | 'ADOCEANService'>,
    name: string,
    data: IArtSponsParams | null,
    isDFP: boolean
): void => {
    const { UANC, services } = window.Inpl.Ad as IAd;
    const service = services[serviceKey];

    UANC instanceof DebugUANC && UANC.info(`[linkSpons] SET name: ${name}; data: ${JSON.stringify(data)}; isDFP: ${isDFP}`);

    if (service) {
        service.linkSponsPendingItems = {
            ...service.linkSponsPendingItems,
            data: {
                ...service.linkSponsPendingItems.data,
                linkSpons: data,
            },
        };
    }

    if (name && name !== 'linkSpons') {
        executeArtSponsCb(name, isDFP);
        addSlotEvent({ name, dfp: isDFP }, null, AD_STATUS.ART_SPONS);
    }
};

/**
 *  Przekazanie konfiguracji o reklamie dla wskazanego linkSpons w zależności od serwera.
 *
 * @param serviceKey - nazwa Adservera
 * @param {string} name - nazwa - linkSpons
 * @param {(data: IArtSponsParams | null) => void | null} cb - opcjonalnie, cb do wywołania dla linkSpons
 * @returns {IArtSponsParams | null} informacja o reklamie
 */
export const getArtSponsParamsFromService = (
    serviceKey: keyof Pick<IServices, 'DFPService' | 'ADOCEANService'>,
    name: string,
    cb: ((data: IArtSponsParams | null) => void) | null = null
): IArtSponsParams | null => {
    const { UANC, services } = window.Inpl.Ad as IAd;
    const service = services[serviceKey];
    const artSponsParam = service?.linkSponsPendingItems.data[name] || null;

    UANC instanceof DebugUANC && UANC.info(`[linkSpons] GET name: ${name}; artSponsParam:${JSON.stringify(artSponsParam)}`);

    if (!artSponsParam && service) {
        service.linkSponsPendingItems = {
            ...service.linkSponsPendingItems,
            dataRequests: {
                ...service.linkSponsPendingItems.dataRequests,
                [name]: cb,
            },
        };
    } else if (cb && isFunctionType(cb)) {
        cb(artSponsParam);
    }

    return artSponsParam;
};

/**
 * Przekazuje nazwy placementów, które są aktywne i możliwe do usunięcia.
 *
 * @param { ServiceSlotType } slotConfig - slot serwisowy do osadzenia
 * @param { string[] } placementsToCheck - nazwy placementów do weryfikacji.
 * @returns { string[] } nazwy placementów
 */
export const getPlacementNamesToDestroy = (slotConfig: ServiceSlotType, placementsToCheck: string[]): string[] => {
    const { Ad } = window.Inpl;

    const services = Ad.services as IServices;
    const serviceName = `${[slotConfig.adServer]}Service` as keyof IServices;
    if (!(serviceName in services)) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[getPlacementNamesToDestroy] ad service for ad server: ${slotConfig.adServer} is not available.`);
        return [];
    }
    const service = services[serviceName] as ServiceType;
    const adUnits = service.adUnits;

    const placementsToDestroy = placementsToCheck.reduce((placements: string[], placementName: string) => {
        const adUnitId = slotConfig.adServer === AD_SERVERS.ADOCEAN ? slotConfig.slotid : placementName;
        return adUnits[adUnitId] && slotConfig.renderedPlacements?.[placementName] ? [...placements, placementName] : placements;
    }, []);

    if (!placementsToDestroy.length) {
        Ad.UANC instanceof DebugUANC && Ad.UANC.warn(`[destroyAd] No placement to destroy for slot ${slotConfig.name}.`);
        return [];
    }

    return placementsToDestroy;
};

/**
 * Funkcja grupująca wykonanie metod np insertAd aby zmniejszyć ilość requestów wykonanych do adserwera
 * timer: timeout w czasie którym grupowane są wywołania metody
 * groupExecution: funkcja która zbiera
 *
 * @param {ServiceSlotType} slotConfig - konfiguracja slotu reklamowego do osadzenia
 * @param {string[]} placementIds - miejsca do osadzenia reklam
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 *
 * @returns {void}
 */
const timers: ITimers = {
    timerADOCEAN: null,
    timerDFP: null,
};
export const groupExecution = (slotConfig: ServiceSlotType, placementIds: string[], targeting: TargetingType | null = null) => {
    const { services, UANC, state } = window.Inpl.Ad;

    const service: IADOCEANService | IDFPService | undefined = services?.[`${slotConfig.adServer}Service`];
    if (!service) return;

    const timer: ReturnType<typeof setTimeout> | null = timers[`timer${slotConfig.adServer}`];
    if (timer) {
        clearTimeout(timer);
    }

    service.groupExecutionItem = service.groupExecutionItem || {};
    service.groupExecutionItem[slotConfig.name] = service.groupExecutionItem[slotConfig.name] || [];
    service.groupExecutionItem[slotConfig.name] = [...service.groupExecutionItem[slotConfig.name], { placementIds, targeting }];

    timers[`timer${slotConfig.adServer}`] = setTimeout(() => {
        const serviceUtils: ServiceUtilsType = getServiceUtils(slotConfig.adServer);
        const copyGroupExecution: IAdsToEmbed = JSON.parse(JSON.stringify(service.groupExecutionItem));

        service.groupExecutionItem = {};

        if (state?.isReloadUanc) return;

        serviceUtils.insertAds(copyGroupExecution);
        UANC instanceof DebugUANC && UANC.table('[groupExecution] Slots provided for defined Ads:', copyGroupExecution);
    }, 100);
};
